import React from 'react';
import PropTypes from 'prop-types';

/**
 * StatusBadge Component
 * 
 * Pre-defined badge types for common use cases:
 * - table: Table number badges (e.g., "Table 3")
 * - checkedIn: "Checked in" (the guest has arrived)
 * - notCheckedIn: "Not checked in" (the guest has not arrived yet)
 *
 * These two show arrival (check-in), not the RSVP answer.
 */
const StatusBadge = ({ type, children, tableNumber }) => {
  const baseClasses = 'inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md whitespace-nowrap shadow-sm h-7';

  const configs = {
    table: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      ),
      text: tableNumber ? `Table ${tableNumber}` : children,
    },
    checkedIn: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ),
      text: 'Checked in',
    },
    notCheckedIn: {
      style: {
        backgroundColor: 'var(--color-neutral)',
        color: 'var(--color-secondary)',
        borderColor: 'var(--color-neutral)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      text: 'Not checked in',
    },
  };

  const config = configs[type];

  return (
    <span className={baseClasses} style={config.style}>
      {config.icon}
      {config.text}
    </span>
  );
};

StatusBadge.propTypes = {
  type: PropTypes.oneOf(['table', 'checkedIn', 'notCheckedIn']).isRequired,
  children: PropTypes.node,
  tableNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default StatusBadge;
